import React, { useCallback, useMemo } from "react";
import { AccountDirectorInfoRow } from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import { ColumnType } from "../../../../../components/ui/baseDataTable";
import {
  addDirectorPartnerRow,
  deleteDirectorPartnerRow,
  selectFMR1DraftAccountDirectorInfoRows,
  updateDirectorPartnerRow,
} from "../../../logic/frm1Slice";
import {
  generateRowId,
  getInsertedSno,
  getInsertedSortOrder,
  getSnoUpdates,
  sortRowsBySortOrder,
} from "../common/utils";
import { FMRTable, FMRTableColumn } from "../common/FMRTable";

export type DirectorPartnerDetailsTableHandle = {
  getDirtyRows: () => AccountDirectorInfoRow[];
  getDirtyUpsertPayload: () => any[];
  getAllRows: () => AccountDirectorInfoRow[];
  markAllClean: () => void;
  resetRows: (nextRows: AccountDirectorInfoRow[]) => void;
};

export interface DirectorPartnerDetailsTableProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onDirtyUpsertPayloadChange?: (payload: any[]) => void;
  initialRows?: AccountDirectorInfoRow[];
}

const buildEmptyRow = (): AccountDirectorInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

export const DirectorPartnerDetailsTable: React.FC<DirectorPartnerDetailsTableProps> = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftAccountDirectorInfoRows);
  const sortedRows = useMemo(() => sortRowsBySortOrder(rows), [rows]);

  const handleAddRow = useCallback((rowIndex: number) => {
    const prevRow = sortedRows[rowIndex];
    const prevSort = prevRow?.sort_order;
    const nextSort = sortedRows[rowIndex + 1]?.sort_order;
    const newRow: AccountDirectorInfoRow = {
      ...buildEmptyRow(),
      sort_order: getInsertedSortOrder(prevSort, nextSort),
      sno: getInsertedSno(prevRow),
    };

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, +1);
    updates.forEach((u) => {
      dispatch(updateDirectorPartnerRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(addDirectorPartnerRow({ row: newRow }));
  }, [dispatch, sortedRows]);

  const handleDeleteRow = useCallback(async (rowIndex: number) => {
    const deletedRow = sortedRows[rowIndex];
    if (!deletedRow) return;

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, -1);
    updates.forEach((u) => {
      dispatch(updateDirectorPartnerRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(deleteDirectorPartnerRow({ id: deletedRow.row_id }));

    if (deletedRow.isAdded) return;
  }, [dispatch, sortedRows]);

  const handleChange = useCallback(
    (rowIndex: number, value: any, field: keyof AccountDirectorInfoRow) => {
      const row = sortedRows[rowIndex];
      if (!row) return;
      dispatch(updateDirectorPartnerRow({ id: row.row_id, changes: { [field]: value } as any }));
    },
    [dispatch, sortedRows],
  );

  const columns: FMRTableColumn<AccountDirectorInfoRow>[] = [
    {
      headerName: "Name of Account",
      field: "name_of_account",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.name_of_account ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_account"),
    },

    {
      headerName: "Sr. No. of Account",
      field: "sr_no_of_account",
      type: "number" as ColumnType,
      isEditable: true,
      getValue: (row) => row.sr_no_of_account ?? null,
      onChange: (rowIndex: number, value: number | null) => handleChange(rowIndex, value, "sr_no_of_account"),
      headerClassName: "min-w-[100px]",
    },
    {
      headerName: "Sr. No.",
      field: "sno",
      type: "text" as ColumnType,
      isEditable: false,
      className: "w-[50px]",
      getValue: (row) => row.sno ?? null,
    },

    {
      headerName: "Name of Director/Partner/Individuals/HUF",
      field: "name_of_director_partner",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.name_of_director_partner ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_director_partner"),
    },

    {
      headerName: "PAN of Director / Partner",
      field: "pan_of_director_partner",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.pan_of_director_partner ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "pan_of_director_partner"),
    },

    {
      headerName: "DIN",
      field: "din",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.din ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "din"),
    },

    {
      headerName: "Director/Partner/Individuals/HUF Address",
      field: "director_partner_address",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.director_partner_address ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "director_partner_address"),
    },
  ];

  return (
    <FMRTable
      columns={columns}
      rows={sortedRows}
      onAdd={handleAddRow}
      onDelete={handleDeleteRow}
      title={"Details of Director/Partner"}
    />
  );
};

DirectorPartnerDetailsTable.displayName = "DirectorPartnerDetailsTable";
